import React, { useCallback, useMemo, useEffect, useRef, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { LayoutRoom } from '@/components/multiplayer/RoomShell'
import { MaybePresenceDock } from '@/components/multiplayer/PresenceLayer'
import { PresenceCanvasOverlay } from '@/components/multiplayer/PresenceCanvasOverlay'
import {
  ReactFlow,
  Node,
  Edge,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  useReactFlow,
  Connection,
  OnConnect,
  OnNodesChange,
  ConnectionLineType,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { useLayoutStore } from '@/stores/layoutStore'
import { useUIStore } from '@/stores/uiStore'
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts'
import DeviceNode from '@/components/canvas/DeviceNode'
import ZoneNode from '@/components/canvas/ZoneNode'
import WallNode, {
  WALL_INPUT_NODE_PREFIX,
  WALL_OUTPUT_NODE_PREFIX,
  EXT_HANDLE_SUFFIX,
  isWallNodeId,
  stripExtHandleSuffix,
  wallKindFromNodeId,
  type WallNodeData,
} from '@/components/canvas/WallNode'
import PeerZoneSatelliteNode, {
  PEER_IN_NODE_PREFIX,
  PEER_OUT_NODE_PREFIX,
  isPeerSatelliteNodeId,
  type PeerSatelliteData,
} from '@/components/canvas/PeerZoneSatelliteNode'
import ConnectionEdge from '@/components/canvas/ConnectionEdge'
import { CanvasToolbar } from '@/components/canvas/CanvasToolbar'
import { RoutingMatrix } from '@/components/views/RoutingMatrix'
import { ConnectionList } from '@/components/views/ConnectionList'
import { Device, Port, Zone } from '@/types/devices'
import type { ZoneConnectionPoint } from '@busswerks/shared'
import { createSeedLayout } from '@/lib/seedData'
import { generateSuggestions, SuggestedConnection } from '@/engine/autoMapper'
import { getLayoutedElements } from '@/engine/layoutEngine'
import { validateConnection } from '@/engine/connectionValidator'
import { SIGNAL_TYPE_COLORS } from '@/constants/colors'
import { AddDeviceDialog } from '@/components/panels/AddDeviceDialog'
import { ManageDevicesPanel } from '@/components/panels/ManageDevicesPanel'
import { ManageZonesDialog } from '@/components/panels/ManageZonesDialog'
import { UserMenu } from '@/components/UserMenu'
import { apiClient, APIError } from '@/lib/api'
import { useAiSuggest, type AILayoutSuggestion } from '@/hooks/useAiSuggest'
import { useEffectiveFeatures } from '@/hooks/useEffectiveFeatures'
import { useMidiStore } from '@/stores/midiStore'
import MidiPanel from '@/components/midi/MidiPanel'
import { DemoCallout } from '@/components/DemoCallout'
import { BusswerksLogo } from '@/components/BusswerksLogo'

const nodeTypes = {
  device: DeviceNode,
  zone: ZoneNode,
  wall: WallNode,
  peerSatellite: PeerZoneSatelliteNode,
}

/**
 * Build the two wall nodes (input + output) for a focused zone.
 * Pure function so we don't accidentally capture store state in the
 * useMemo closure.
 */
function buildWallNodes(
  zone: Zone,
  zoneDevices: Device[],
  inputPos: { x: number; y: number },
  outputPos: { x: number; y: number },
): Node[] {
  const cps = zone.connectionPoints ?? []
  const inputPoints = cps.filter(cp => cp.direction === 'input')
  const outputPoints = cps.filter(cp => cp.direction === 'output')
  // Resolve human labels for each cp by looking up the internal
  // device+port on the live device list. Done once at projection
  // time so cp.name can stay terse (just the port name) in storage
  // while the wall still shows "<device> · <port>".
  const labelsFor = (
    subset: typeof cps,
  ): Record<string, { deviceName: string; portName: string }> => {
    const out: Record<string, { deviceName: string; portName: string }> = {}
    for (const cp of subset) {
      const dev = zoneDevices.find(d => d.id === cp.internalDeviceId)
      if (!dev) continue
      const port =
        dev.inputs.find(p => p.id === cp.internalPortId) ||
        dev.outputs.find(p => p.id === cp.internalPortId)
      if (!port) continue
      out[cp.id] = { deviceName: dev.name, portName: port.name }
    }
    return out
  }
  return [
    {
      id: `${WALL_INPUT_NODE_PREFIX}${zone.id}`,
      type: 'wall',
      position: inputPos,
      draggable: false,
      selectable: false,
      data: {
        wallKind: 'input',
        zoneId: zone.id,
        zoneName: zone.name,
        zoneColor: zone.color,
        connectionPoints: inputPoints,
        labels: labelsFor(inputPoints),
      } satisfies WallNodeData,
    },
    {
      id: `${WALL_OUTPUT_NODE_PREFIX}${zone.id}`,
      type: 'wall',
      position: outputPos,
      draggable: false,
      selectable: false,
      data: {
        wallKind: 'output',
        zoneId: zone.id,
        zoneName: zone.name,
        zoneColor: zone.color,
        connectionPoints: outputPoints,
        labels: labelsFor(outputPoints),
      } satisfies WallNodeData,
    },
  ]
}
const edgeTypes = { connection: ConnectionEdge }

const LOCAL_STORAGE_KEY = 'audio-cfg-v2-dev-layout'
// Bump this when seed data changes to force a refresh of stale cached layouts
const SEED_DATA_VERSION = 6

function loadLocalLayout() {
  if (typeof window === 'undefined') return null
  try {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY)
    if (stored) {
      const parsed = JSON.parse(stored)
      // If stored data is from an older seed version, discard it
      if (parsed.seedVersion !== SEED_DATA_VERSION) return null
      return parsed
    }
  } catch {
    // ignore
  }
  return null
}

function saveLocalLayout(devices: Device[], connections: any[], zones: any[]) {
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem(
      LOCAL_STORAGE_KEY,
      JSON.stringify({
        devices,
        connections,
        zones,
        seedVersion: SEED_DATA_VERSION,
        savedAt: new Date().toISOString(),
      }),
    )
  } catch {
    // ignore
  }
}

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error'

export default function EditorClient({ id }: { id: string }) {
  const devices = useLayoutStore(s => s.devices)
  const connections = useLayoutStore(s => s.connections)
  const zones = useLayoutStore(s => s.zones)
  const addDevice = useLayoutStore(s => s.addDevice)
  const deleteDevice = useLayoutStore(s => s.deleteDevice)
  const updateDevice = useLayoutStore(s => s.updateDevice)
  const updateDevicePosition = useLayoutStore(s => s.updateDevicePosition)
  const updateZone = useLayoutStore(s => s.updateZone)
  const addConnection = useLayoutStore(s => s.addConnection)
  const removeConnection = useLayoutStore(s => s.removeConnection)
  const hydrate = useLayoutStore(s => s.hydrate)
  const layoutName = useLayoutStore(s => s.layoutName)
  const mutationQueue = useLayoutStore(s => s.mutationQueue)
  const selectedIds = useUIStore(s => s.selectedIds)
  const viewMode = useUIStore(s => s.viewMode)
  const focusedZoneId = useUIStore(s => s.focusedZoneId)
  const setFocusedZoneId = useUIStore(s => s.setFocusedZoneId)

  const midiPanelOpen = useMidiStore(s => s.midiPanelOpen)
  const toggleMidiPanel = useMidiStore(s => s.toggleMidiPanel)

  // Tier-gated features: read through the effective-features hook so
  // DEV tier overrides can exercise gating paths. Flips instantly
  // after Stripe Checkout completes in normal use.
  const { features } = useEffectiveFeatures()

  const [initialized, setInitialized] = useState(false)
  const [layoutRole, setLayoutRole] = useState<'owner' | 'editor' | 'viewer'>(
    'owner',
  )
  const isReadOnly = layoutRole === 'viewer'
  const [suggestions, setSuggestions] = useState<SuggestedConnection[]>([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [addDeviceOpen, setAddDeviceOpen] = useState(false)
  const [manageDevicesOpen, setManageDevicesOpen] = useState(false)
  const [zoneDialogOpen, setZoneDialogOpen] = useState(false)
  const [editingZone, setEditingZone] = useState<Zone | null>(null)
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle')
  const [saveError, setSaveError] = useState<string | null>(null)

  // React Flow imperative API for framing the viewport when content
  // changes and for querying node intersections on drag-and-drop.
  const { fitView, getIntersectingNodes, getNode } = useReactFlow()
  // Track the previous device count so we can detect "a device was added"
  // and refit the canvas around the new content.
  const prevDeviceCountRef = useRef(0)

  // Enable keyboard shortcuts
  useKeyboardShortcuts()

  const isLocalDev = id === 'local-dev'

  // Initialize: for /editor/local-dev, seed from localStorage; for real
  // layout ids, fetch the layout from the API and hydrate the store.
  useEffect(() => {
    let cancelled = false
    setInitialized(false)

    if (isLocalDev) {
      const stored = loadLocalLayout()
      if (stored && stored.devices?.length > 0) {
        hydrate({
          ...createSeedLayout(),
          devices: stored.devices,
          connections: stored.connections || [],
          zones: stored.zones || [],
        })
      } else {
        hydrate(createSeedLayout())
      }
      setInitialized(true)
      return () => {
        cancelled = true
      }
    }

    apiClient
      .get<{
        id: string
        metadata?: {
          name?: string
          description?: string
          version?: number
        }
        devices?: Device[]
        connections?: unknown[]
        zones?: Zone[]
        role?: 'owner' | 'editor' | 'viewer'
      }>(`/api/v1/layouts/${id}`)
      .then(layout => {
        if (cancelled) return
        const seed = createSeedLayout()
        hydrate({
          ...seed,
          id: layout.id,
          name: layout.metadata?.name ?? seed.name,
          description: layout.metadata?.description ?? '',
          // Critical: take the version from the server response, not the
          // seed's placeholder. Sending a stale version is what was
          // causing the save-on-edit path to 409 on every first save.
          version: layout.metadata?.version ?? 0,
          devices: (layout.devices as Device[]) ?? [],
          connections: (layout.connections as typeof seed.connections) ?? [],
          zones: (layout.zones as Zone[]) ?? [],
        })
        setLayoutRole(layout.role ?? 'owner')
        setInitialized(true)
      })
      .catch(err => {
        if (cancelled) return
        console.error('Failed to load layout', id, err)
        hydrate({
          ...createSeedLayout(),
          devices: [],
          connections: [],
          zones: [],
        })
        setInitialized(true)
      })

    return () => {
      cancelled = true
    }
  }, [id, isLocalDev, hydrate])

  // Auto-save to localStorage on changes — only for the local-dev sandbox.
  useEffect(() => {
    if (!isLocalDev || !initialized || devices.length === 0) return
    const timer = setTimeout(
      () => saveLocalLayout(devices, connections, zones),
      1000,
    )
    return () => clearTimeout(timer)
  }, [devices, connections, zones, initialized, isLocalDev])

  // Auto-save cloud layouts back to the API. Debounced on the mutation
  // queue so we batch rapid edits (drag, connect, etc.) into one PUT.
  // Reads the live store state at flush time so the serialised payload
  // always reflects the latest positions, not a stale closure snapshot.
  useEffect(() => {
    if (isLocalDev || !initialized) return
    if (isReadOnly) return
    if (mutationQueue.length === 0) return

    const timer = setTimeout(async () => {
      const state = useLayoutStore.getState()
      if (!state.layoutId) return
      setSaveStatus('saving')
      setSaveError(null)
      try {
        const updated = await apiClient.put<{ version: number }>(
          `/api/v1/layouts/${state.layoutId}`,
          {
            devices: state.devices,
            connections: state.connections,
            zones: state.zones,
            version: state.layoutVersion,
          },
        )
        // Clear the queue and adopt the server's new version number so
        // the next save passes the optimistic-concurrency check.
        useLayoutStore.setState({
          layoutVersion: updated.version,
          mutationQueue: [],
        })
        setSaveStatus('saved')
      } catch (err) {
        // On a 409 version conflict, refetch the current server state
        // and adopt its version so the next mutation can save cleanly.
        // The user loses any unsaved in-memory edits (they're replaced
        // by the server's version), but that's the safe default —
        // without it the editor is permanently stuck.
        if (err instanceof APIError && err.status === 409) {
          try {
            const fresh = await apiClient.get<{
              metadata?: { version?: number; name?: string }
              devices?: Device[]
              connections?: unknown[]
              zones?: Zone[]
            }>(`/api/v1/layouts/${state.layoutId}`)
            const seed = createSeedLayout()
            hydrate({
              ...seed,
              id: state.layoutId,
              name: fresh.metadata?.name ?? state.layoutName,
              version: fresh.metadata?.version ?? 0,
              devices: (fresh.devices as Device[]) ?? [],
              connections: (fresh.connections as typeof seed.connections) ?? [],
              zones: (fresh.zones as Zone[]) ?? [],
            })
            setSaveStatus('idle')
            setSaveError(null)
            return
          } catch (refetchErr) {
            console.error('Failed to refetch after 409', refetchErr)
          }
        }
        console.error('Failed to save layout', err)
        setSaveError(err instanceof Error ? err.message : 'Save failed')
        setSaveStatus('error')
      }
    }, 1000)

    return () => clearTimeout(timer)
  }, [mutationQueue, initialized, isLocalDev, isReadOnly])

  // Convert zones to compact React Flow summary cards. In the overview
  // a zone is drawn as a single node; individual devices inside it are
  // hidden and surfaced via the drilldown view. Device counts come
  // from the live device list (source of truth) so the summary never
  // drifts out of sync with `zone.deviceIds`.
  const openEditZone = useCallback((zone: Zone) => {
    setEditingZone(zone)
    setZoneDialogOpen(true)
  }, [])

  const zoneNodes: Node[] = useMemo(() => {
    // Build per-zone cp routing metadata from the current cross-zone
    // connections so the overview zone cards place their handles on
    // the edge facing the matched peer and in an order that minimises
    // edge crossings. Without this, handles stack in stored insertion
    // order on a fixed edge and cross-zone connections bundle into a
    // crossed mess in the middle of the canvas.
    const zoneById = new Map(zones.map(z => [z.id, z]))

    // For each cp id, collect the peer zone ids it's connected to
    // (via cross-zone Connection rows). A cp can feed multiple
    // peers; we pick the one with the most traffic for edge choice.
    // { cpId → Map<peerZoneId, peerCpId> }
    const cpPeers = new Map<string, Map<string, string>>()
    for (const conn of connections) {
      const srcDev = devices.find(d => d.id === conn.sourceDeviceId)
      const tgtDev = devices.find(d => d.id === conn.targetDeviceId)
      if (!srcDev || !tgtDev) continue
      if (!srcDev.zoneId || !tgtDev.zoneId) continue
      if (srcDev.zoneId === tgtDev.zoneId) continue
      const srcZone = zoneById.get(srcDev.zoneId)
      const tgtZone = zoneById.get(tgtDev.zoneId)
      if (!srcZone || !tgtZone) continue
      const srcCp = srcZone.connectionPoints?.find(
        cp =>
          cp.internalDeviceId === srcDev.id &&
          cp.internalPortId === conn.sourcePortId,
      )
      const tgtCp = tgtZone.connectionPoints?.find(
        cp =>
          cp.internalDeviceId === tgtDev.id &&
          cp.internalPortId === conn.targetPortId,
      )
      if (!srcCp || !tgtCp) continue
      const srcMap = cpPeers.get(srcCp.id) ?? new Map<string, string>()
      srcMap.set(tgtDev.zoneId, tgtCp.id)
      cpPeers.set(srcCp.id, srcMap)
      const tgtMap = cpPeers.get(tgtCp.id) ?? new Map<string, string>()
      tgtMap.set(srcDev.zoneId, srcCp.id)
      cpPeers.set(tgtCp.id, tgtMap)
    }

    // Pick the edge ('left' | 'right' | 'top' | 'bottom') where
    // `thisZone`'s cp should sit, based on the predominant peer
    // zone's position relative to this zone. Falls back to the
    // stored cp.edge when there's no cross-zone connection.
    const pickEdge = (
      thisZoneId: string,
      cpId: string,
      fallback: 'left' | 'right' | 'top' | 'bottom',
    ): 'left' | 'right' | 'top' | 'bottom' => {
      const peers = cpPeers.get(cpId)
      if (!peers || peers.size === 0) return fallback
      const thisZone = zoneById.get(thisZoneId)
      if (!thisZone?.position) return fallback
      // Pick any peer (first) — if there are multiple, we still
      // need a single edge, and any of them will draw cleanly.
      const [peerZoneId] = peers.keys()
      const peerZone = zoneById.get(peerZoneId)
      if (!peerZone?.position) return fallback
      const dx = peerZone.position.x - thisZone.position.x
      const dy = peerZone.position.y - thisZone.position.y
      if (Math.abs(dx) >= Math.abs(dy)) {
        return dx >= 0 ? 'right' : 'left'
      }
      return dy >= 0 ? 'bottom' : 'top'
    }

    return zones
      .filter(zone => zone.position)
      .map(zone => {
        const zoneDevices = devices.filter(d => d.zoneId === zone.id)

        // Group this zone's cps by chosen edge, then sort each
        // bucket by the peer cp id to give matched pairs a stable
        // relative ordering. Sorting both sides of a peer pair by
        // the same key produces zig-free cross-zone edges in most
        // cases.
        const cps = zone.connectionPoints ?? []
        const routedCps = cps.map(cp => {
          const edge = pickEdge(zone.id, cp.id, cp.edge ?? 'right')
          const peers = cpPeers.get(cp.id)
          // Sort key: the first matched peer cp id. Falls back to
          // cp.id so unmatched cps stay grouped at the end.
          const sortKey = peers
            ? (Array.from(peers.values())[0] ?? cp.id)
            : `zzz-${cp.id}`
          return { cp, edge, sortKey }
        })

        // Group by (edge, direction) so input and output cps on
        // the same edge don't interleave — the zone card renders
        // both input handles (target) and output handles (source)
        // on whatever edge pickEdge chose.
        const grouped = new Map<string, typeof routedCps>()
        for (const entry of routedCps) {
          const key = `${entry.edge}|${entry.cp.direction}`
          const arr = grouped.get(key) ?? []
          arr.push(entry)
          grouped.set(key, arr)
        }
        for (const arr of grouped.values()) {
          arr.sort((a, b) => (a.sortKey < b.sortKey ? -1 : 1))
        }

        // Assign positions evenly within each (edge, direction)
        // bucket, biased toward the middle of the edge so the
        // handles stay within the rounded corners of the card.
        const positioned: typeof cps = []
        for (const [, arr] of grouped) {
          const n = arr.length
          arr.forEach((entry, i) => {
            const t = n === 1 ? 0.5 : 0.12 + (0.76 * i) / (n - 1)
            positioned.push({
              ...entry.cp,
              edge: entry.edge,
              position: t,
            })
          })
        }

        return {
          id: zone.id,
          type: 'zone',
          position: zone.position ?? { x: 0, y: 0 },
          data: {
            ...zone,
            connectionPoints: positioned,
            // Force the compact ZoneNode layout regardless of the
            // zone's stored collapsed flag.
            collapsed: true,
            deviceCount: zoneDevices.length,
            // Lightweight view of member devices so ZoneNode can
            // render the list + category dot without reaching
            // back into the store.
            memberDevices: zoneDevices.slice(0, 20).map(d => ({
              id: d.id,
              name: d.name,
              category: d.category,
            })),
            onEdit: () => openEditZone(zone),
          },
          draggable: true,
          selectable: false,
        }
      })
  }, [zones, devices, connections, openEditZone])

  // Convert devices to React Flow nodes
  const deviceNodes: Node[] = useMemo(
    () =>
      devices.map(device => ({
        id: device.id,
        data: {
          ...device,
          isSelected: selectedIds.has(device.id),
          onDelete: (id: string) => deleteDevice(id),
          onEdit: (d: Device) => console.log('Edit device:', d),
        },
        position: device.position || { x: 0, y: 0 },
        type: 'device',
      })),
    [devices, selectedIds, deleteDevice],
  )

  // Viewport filtering:
  //
  //   Overview (focusedZoneId === null)
  //     - Each zone renders as a compact summary card.
  //     - Devices with NO zoneId ("unassigned") render as individual
  //       devices at the layout level.
  //     - Devices that belong to a zone are hidden — they live inside
  //       the summary card and are only visible via drilldown.
  //
  //   Drilldown (focusedZoneId === "zone-…")
  //     - All zone containers are hidden (you are already inside one).
  //     - Only devices whose zoneId matches the focused zone render.
  //     - Unassigned devices are not shown.
  const focusedZone = useMemo(
    () => (focusedZoneId ? zones.find(z => z.id === focusedZoneId) : null),
    [focusedZoneId, zones],
  )
  const visibleDeviceIds = useMemo(() => {
    if (focusedZoneId) {
      return new Set(
        devices.filter(d => d.zoneId === focusedZoneId).map(d => d.id),
      )
    }
    // Overview: only unassigned devices render at the top level.
    return new Set(devices.filter(d => !d.zoneId).map(d => d.id))
  }, [devices, focusedZoneId])
  const filteredZoneNodes: Node[] = useMemo(
    () => (focusedZoneId ? [] : zoneNodes),
    [zoneNodes, focusedZoneId],
  )
  const filteredDeviceNodes: Node[] = useMemo(
    () => deviceNodes.filter(n => visibleDeviceIds.has(n.id)),
    [deviceNodes, visibleDeviceIds],
  )

  // Synthesise two wall nodes whenever the user is drilled into a
  // zone. Walls are pure-UI React Flow nodes built from the zone's
  // connectionPoints array — they aren't stored anywhere. Position
  // them flanking the device bounding box so cross-zone links have
  // visible endpoints.
  // Derive wall geometry once and reuse it for the peer satellite
  // memo below. Having both memos depend on the same derived
  // coordinates keeps satellites anchored to their walls even as
  // the device cluster shifts around on drags.
  const wallGeometry = useMemo(() => {
    if (!focusedZoneId || !focusedZone) return null
    const zoneDevices = devices.filter(d => d.zoneId === focusedZoneId)
    // WallNode's own min-height — offset node position to vertically
    // center the wall on the device cluster instead of top-aligning.
    const wallHeight = 360
    // Approximate device card height for vertical-center math.
    const approxDeviceHeight = 200
    if (zoneDevices.length === 0) {
      const wallY = -wallHeight / 2
      return {
        zoneDevices,
        wallHeight,
        wallY,
        left: { x: -320, y: wallY },
        right: { x: 400, y: wallY },
      }
    }
    const xs = zoneDevices.map(d => d.position?.x ?? 0)
    const ys = zoneDevices.map(d => d.position?.y ?? 0)
    const minX = Math.min(...xs)
    const maxX = Math.max(...xs)
    const minY = Math.min(...ys)
    const maxY = Math.max(...ys)
    const midY = (minY + maxY) / 2 + approxDeviceHeight / 2
    const wallY = midY - wallHeight / 2
    return {
      zoneDevices,
      wallHeight,
      wallY,
      left: { x: minX - 260, y: wallY },
      right: { x: maxX + 340, y: wallY },
    }
  }, [focusedZoneId, focusedZone, devices])

  const wallNodes: Node[] = useMemo(() => {
    if (!focusedZone || !wallGeometry) return []
    return buildWallNodes(
      focusedZone,
      wallGeometry.zoneDevices,
      wallGeometry.left,
      wallGeometry.right,
    )
  }, [focusedZone, wallGeometry])

  // Peer zone satellites. For every other zone that has a
  // cross-zone Connection touching the focused zone, render a
  // satellite node showing only that peer's cps in the direction
  // that interacts with the focused zone. Each satellite carries
  // data-drop-zone-target={peerZoneId} so the existing drag-drop
  // reassignment path in handleNodeDragStop moves devices to it
  // without any new code.
  const peerSatelliteNodes: Node[] = useMemo(() => {
    if (!focusedZoneId || !focusedZone || !wallGeometry) return []

    // Collect the set of peer zone ids that have *any* cross-zone
    // traffic with the focused zone (either direction). For every
    // such peer we emit BOTH a peer-out satellite (near the
    // focused input wall, showing peer's output cps) AND a peer-in
    // satellite (near the focused output wall, showing peer's
    // input cps), regardless of which direction the existing
    // traffic goes. This gives users a place to route NEW cross-
    // zone connections from drilldown view without leaving it,
    // and keeps satellites symmetric between peers.
    const peerZoneIds = new Set<string>()
    for (const conn of connections) {
      const srcDev = devices.find(d => d.id === conn.sourceDeviceId)
      const tgtDev = devices.find(d => d.id === conn.targetDeviceId)
      if (!srcDev || !tgtDev) continue
      if (srcDev.zoneId === tgtDev.zoneId) continue
      if (srcDev.zoneId === focusedZoneId && tgtDev.zoneId) {
        peerZoneIds.add(tgtDev.zoneId)
      } else if (tgtDev.zoneId === focusedZoneId && srcDev.zoneId) {
        peerZoneIds.add(srcDev.zoneId)
      }
    }

    const wallHeight = wallGeometry.wallHeight
    const satSpacingY = wallHeight + 24
    const nodes: Node[] = []

    const makeLabels = (
      cps: ZoneConnectionPoint[],
      peerDevices: Device[],
    ): Record<string, { deviceName: string; portName: string }> => {
      const out: Record<string, { deviceName: string; portName: string }> = {}
      for (const cp of cps) {
        const dev = peerDevices.find(d => d.id === cp.internalDeviceId)
        if (!dev) continue
        const port =
          dev.inputs.find(p => p.id === cp.internalPortId) ||
          dev.outputs.find(p => p.id === cp.internalPortId)
        if (!port) continue
        out[cp.id] = { deviceName: dev.name, portName: port.name }
      }
      return out
    }

    // Sort peer ids for stable stacking order across renders.
    const sortedPeers = Array.from(peerZoneIds).sort()
    let leftStackIdx = 0
    let rightStackIdx = 0
    for (const peerZoneId of sortedPeers) {
      const peerZone = zones.find(z => z.id === peerZoneId)
      if (!peerZone) continue
      const peerDevices = devices.filter(d => d.zoneId === peerZoneId)
      const peerOutputs = (peerZone.connectionPoints ?? []).filter(
        cp => cp.direction === 'output',
      )
      const peerInputs = (peerZone.connectionPoints ?? []).filter(
        cp => cp.direction === 'input',
      )

      // peer-out satellite — shown near the focused input wall,
      // lists the peer's OUTPUT cps (signal flows peer→focused).
      // Always emitted, even when the peer currently has zero
      // output exposures, so the presence of the peer is visible
      // on both sides of the focused zone.
      nodes.push({
        id: `${PEER_OUT_NODE_PREFIX}${focusedZoneId}-${peerZoneId}`,
        type: 'peerSatellite',
        position: {
          x: wallGeometry.left.x - 300,
          y: wallGeometry.wallY + leftStackIdx * satSpacingY,
        },
        draggable: false,
        selectable: false,
        data: {
          peerZoneId,
          peerZoneName: peerZone.name,
          peerZoneColor: peerZone.color,
          side: 'input',
          connectionPoints: peerOutputs,
          labels: makeLabels(peerOutputs, peerDevices),
        } satisfies PeerSatelliteData,
      })
      leftStackIdx += 1

      // peer-in satellite — near the focused output wall, lists
      // peer's INPUT cps.
      nodes.push({
        id: `${PEER_IN_NODE_PREFIX}${focusedZoneId}-${peerZoneId}`,
        type: 'peerSatellite',
        position: {
          x: wallGeometry.right.x + 300,
          y: wallGeometry.wallY + rightStackIdx * satSpacingY,
        },
        draggable: false,
        selectable: false,
        data: {
          peerZoneId,
          peerZoneName: peerZone.name,
          peerZoneColor: peerZone.color,
          side: 'output',
          connectionPoints: peerInputs,
          labels: makeLabels(peerInputs, peerDevices),
        } satisfies PeerSatelliteData,
      })
      rightStackIdx += 1
    }

    return nodes
  }, [focusedZoneId, focusedZone, wallGeometry, connections, devices, zones])

  // Combine zone, wall, satellite, and device nodes (zones first = behind)
  const allNodes: Node[] = useMemo(
    () => [
      ...filteredZoneNodes,
      ...wallNodes,
      ...peerSatelliteNodes,
      ...filteredDeviceNodes,
    ],
    [filteredZoneNodes, wallNodes, peerSatelliteNodes, filteredDeviceNodes],
  )

  // Edge projection. In drilldown we render real device-to-device
  // connections that live entirely inside the focused zone. In the
  // overview we collapse cross-zone traffic: each connection is mapped
  // to its visible endpoint (the containing zone if the device is in
  // one, otherwise the device itself) and duplicate edges between the
  // same pair of endpoints are aggregated into a single line.
  const edges: Edge[] = useMemo(() => {
    if (focusedZoneId) {
      // Drilldown: render two kinds of edges.
      //
      //   1. Real device-to-device connections that live entirely
      //      inside the focused zone.
      //   2. Synthetic "exposure" edges from each exposed device
      //      port to the matching handle on its wall. These aren't
      //      stored anywhere — they're derived from the focused
      //      zone's connectionPoints array every render so the
      //      user can see which ports are mapped to the wall.
      const intra = connections
        .filter(
          c =>
            visibleDeviceIds.has(c.sourceDeviceId) &&
            visibleDeviceIds.has(c.targetDeviceId),
        )
        .map(conn => {
          const sourceDevice = devices.find(d => d.id === conn.sourceDeviceId)
          const sourcePort = sourceDevice?.outputs.find(
            p => p.id === conn.sourcePortId,
          )
          const targetDevice = devices.find(d => d.id === conn.targetDeviceId)
          const targetPort = targetDevice?.inputs.find(
            p => p.id === conn.targetPortId,
          )
          const color = sourcePort
            ? SIGNAL_TYPE_COLORS[sourcePort.signalType]
            : '#999'
          return {
            id: conn.id,
            source: conn.sourceDeviceId,
            sourceHandle: conn.sourcePortId,
            target: conn.targetDeviceId,
            targetHandle: conn.targetPortId,
            type: 'connection',
            style: { stroke: color, strokeWidth: 2 },
            data: {
              onDelete: (edgeId: string) => removeConnection(edgeId),
              signalType: conn.signalType,
              sourceDeviceName: sourceDevice?.name,
              sourcePortName: sourcePort?.name,
              targetDeviceName: targetDevice?.name,
              targetPortName: targetPort?.name,
            },
          }
        })

      const exposureEdges: Edge[] = []
      if (focusedZone) {
        const inputWallId = `${WALL_INPUT_NODE_PREFIX}${focusedZone.id}`
        const outputWallId = `${WALL_OUTPUT_NODE_PREFIX}${focusedZone.id}`
        for (const cp of focusedZone.connectionPoints ?? []) {
          const dev = devices.find(d => d.id === cp.internalDeviceId)
          if (!dev || dev.zoneId !== focusedZoneId) continue
          const color = SIGNAL_TYPE_COLORS[cp.signalType] ?? '#999'
          // Pair the edge so React Flow's strict-mode source→target
          // rule holds:
          //   - Output exposure: device port is a source, wall is target.
          //   - Input exposure:  wall handle is a source, device port is target.
          if (cp.direction === 'output') {
            exposureEdges.push({
              id: `exposure-${cp.id}`,
              source: dev.id,
              sourceHandle: cp.internalPortId,
              target: outputWallId,
              targetHandle: cp.id,
              type: 'connection',
              style: {
                stroke: color,
                strokeWidth: 2,
                strokeDasharray: '4 3',
                opacity: 0.8,
              },
              data: { signalType: cp.signalType },
            })
          } else {
            exposureEdges.push({
              id: `exposure-${cp.id}`,
              source: inputWallId,
              sourceHandle: cp.id,
              target: dev.id,
              targetHandle: cp.internalPortId,
              type: 'connection',
              style: {
                stroke: color,
                strokeWidth: 2,
                strokeDasharray: '4 3',
                opacity: 0.8,
              },
              data: { signalType: cp.signalType },
            })
          }
        }
      }

      // Cross-zone projected edges. For each cross-zone connection
      // touching the focused zone, draw an edge from the focused
      // wall's cp handle to the matching handle on the peer
      // satellite. This is pure projection — connections stay
      // device-to-device in storage. React Flow happily renders
      // any edge by source/target node id pairs regardless of
      // handle source/target typing (the strict-mode rule only
      // applies to user-initiated drags).
      const crossZoneEdges: Edge[] = []
      if (focusedZone) {
        const inputWallId = `${WALL_INPUT_NODE_PREFIX}${focusedZone.id}`
        const outputWallId = `${WALL_OUTPUT_NODE_PREFIX}${focusedZone.id}`
        const cpForDevicePort = (
          zoneId: string | undefined,
          deviceId: string,
          portId: string,
        ) => {
          if (!zoneId) return null
          const z = zones.find(x => x.id === zoneId)
          return (
            z?.connectionPoints?.find(
              cp =>
                cp.internalDeviceId === deviceId &&
                cp.internalPortId === portId,
            ) ?? null
          )
        }
        for (const conn of connections) {
          const srcDev = devices.find(d => d.id === conn.sourceDeviceId)
          const tgtDev = devices.find(d => d.id === conn.targetDeviceId)
          if (!srcDev || !tgtDev) continue
          if (srcDev.zoneId === tgtDev.zoneId) continue
          if (
            srcDev.zoneId !== focusedZoneId &&
            tgtDev.zoneId !== focusedZoneId
          )
            continue

          const color = SIGNAL_TYPE_COLORS[conn.signalType ?? 'audio'] ?? '#999'
          const style = {
            stroke: color,
            strokeWidth: 2,
            strokeDasharray: '4 3',
            opacity: 0.8,
          }

          if (srcDev.zoneId === focusedZoneId && tgtDev.zoneId) {
            // focused output wall EXTERNAL handle → peer-in satellite
            const focusedCp = cpForDevicePort(
              focusedZoneId,
              srcDev.id,
              conn.sourcePortId,
            )
            const peerCp = cpForDevicePort(
              tgtDev.zoneId,
              tgtDev.id,
              conn.targetPortId,
            )
            if (!focusedCp || !peerCp) continue
            crossZoneEdges.push({
              id: `xz-${conn.id}`,
              source: outputWallId,
              sourceHandle: `${focusedCp.id}${EXT_HANDLE_SUFFIX}`,
              target: `${PEER_IN_NODE_PREFIX}${focusedZoneId}-${tgtDev.zoneId}`,
              targetHandle: peerCp.id,
              type: 'connection',
              style,
              data: { signalType: conn.signalType },
            })
          } else if (tgtDev.zoneId === focusedZoneId && srcDev.zoneId) {
            // peer-out satellite → focused input wall EXTERNAL handle
            const focusedCp = cpForDevicePort(
              focusedZoneId,
              tgtDev.id,
              conn.targetPortId,
            )
            const peerCp = cpForDevicePort(
              srcDev.zoneId,
              srcDev.id,
              conn.sourcePortId,
            )
            if (!focusedCp || !peerCp) continue
            crossZoneEdges.push({
              id: `xz-${conn.id}`,
              source: `${PEER_OUT_NODE_PREFIX}${focusedZoneId}-${srcDev.zoneId}`,
              sourceHandle: peerCp.id,
              target: inputWallId,
              targetHandle: `${focusedCp.id}${EXT_HANDLE_SUFFIX}`,
              type: 'connection',
              style,
              data: { signalType: conn.signalType },
            })
          }
        }
      }

      return [...intra, ...exposureEdges, ...crossZoneEdges]
    }

    // Overview: map each device endpoint to its zone (if any) and, if
    // the port is exposed on that zone, to the matching boundary
    // handle. Connections that live entirely inside one zone are
    // hidden — they only appear in drilldown.
    const endpointNodeId = (deviceId: string): string | null => {
      const dev = devices.find(d => d.id === deviceId)
      if (!dev) return null
      return dev.zoneId ?? dev.id
    }
    // For a given device+port, find the zone ConnectionPoint id that
    // exposes it at the zone boundary (or null if the device is not
    // in a zone / the port isn't exposed).
    const handleForEndpoint = (
      deviceId: string,
      portId: string,
    ): string | null => {
      const dev = devices.find(d => d.id === deviceId)
      if (!dev?.zoneId) return null
      const zone = zones.find(z => z.id === dev.zoneId)
      const cp = zone?.connectionPoints?.find(
        p => p.internalDeviceId === deviceId && p.internalPortId === portId,
      )
      return cp?.id ?? null
    }

    // Aggregate cross-zone traffic by (src endpoint + handle, tgt
    // endpoint + handle). Count occurrences per signal type so we
    // can pick a dominant type for stroke color / dash pattern and
    // show a per-signal-type breakdown for multi-signal bundles.
    type Agg = {
      count: number
      signalTypeCounts: Map<string, number>
      sourceHandle: string | null
      targetHandle: string | null
    }
    const groups = new Map<string, Agg>()
    for (const conn of connections) {
      const src = endpointNodeId(conn.sourceDeviceId)
      const tgt = endpointNodeId(conn.targetDeviceId)
      if (!src || !tgt || src === tgt) continue
      const srcHandle = handleForEndpoint(
        conn.sourceDeviceId,
        conn.sourcePortId,
      )
      const tgtHandle = handleForEndpoint(
        conn.targetDeviceId,
        conn.targetPortId,
      )
      const key = `${src}/${srcHandle ?? '*'}→${tgt}/${tgtHandle ?? '*'}`
      const sourceDevice = devices.find(d => d.id === conn.sourceDeviceId)
      const sourcePort = sourceDevice?.outputs.find(
        p => p.id === conn.sourcePortId,
      )
      const signalType = conn.signalType ?? sourcePort?.signalType ?? 'audio'
      const existing = groups.get(key)
      if (existing) {
        existing.count += 1
        existing.signalTypeCounts.set(
          signalType,
          (existing.signalTypeCounts.get(signalType) ?? 0) + 1,
        )
      } else {
        groups.set(key, {
          count: 1,
          signalTypeCounts: new Map([[signalType, 1]]),
          sourceHandle: srcHandle,
          targetHandle: tgtHandle,
        })
      }
    }
    return Array.from(groups.entries()).map(([key, agg]) => {
      const [srcPart, tgtPart] = key.split('→')
      const [src] = srcPart.split('/')
      const [tgt] = tgtPart.split('/')
      // Dominant signal type decides color + dash pattern — same
      // visual language the drilldown uses. Ties break by Map
      // iteration order which is insertion order, which is
      // effectively first-seen.
      let dominant = 'audio'
      let dominantCount = 0
      for (const [st, c] of agg.signalTypeCounts) {
        if (c > dominantCount) {
          dominant = st
          dominantCount = c
        }
      }
      const color =
        SIGNAL_TYPE_COLORS[dominant as keyof typeof SIGNAL_TYPE_COLORS] ??
        '#999'
      return {
        id: `agg-${key}`,
        source: src,
        target: tgt,
        sourceHandle: agg.sourceHandle ?? undefined,
        targetHandle: agg.targetHandle ?? undefined,
        type: 'connection',
        style: { stroke: color, strokeWidth: 2 },
        data: {
          signalType: dominant,
          bundleCount: agg.count,
          // ConnectionEdge reads color out of data.sourceHandle.style
          // when present, so mirror the chosen dominant color here
          // to make overview edges colorise the same way drilldown
          // single-connection edges do.
          sourceHandle: {
            id: 'agg',
            position: 'right',
            style: { background: color },
          },
        },
      }
    })
  }, [
    connections,
    devices,
    focusedZone,
    focusedZoneId,
    visibleDeviceIds,
    removeConnection,
    zones,
  ])

  const [nodesState, setNodes, onNodesChange] = useNodesState(allNodes)
  const [edgesState, setEdges, onEdgesChange] = useEdgesState(edges)

  useEffect(() => {
    setNodes(allNodes)
  }, [allNodes, setNodes])
  useEffect(() => {
    setEdges(edges)
  }, [edges, setEdges])

  const handleNodesChange: OnNodesChange = useCallback(
    changes => {
      onNodesChange(changes)
      changes.forEach(change => {
        if (change.type === 'position' && change.position && !change.dragging) {
          // Walls are synthetic, non-draggable UI nodes — they have
          // no persistent position to update, so skip them here so
          // we don't accidentally feed their synthetic ids into
          // updateDevicePosition.
          if (isWallNodeId(change.id) || isPeerSatelliteNodeId(change.id))
            return
          if (change.id.startsWith('zone-')) {
            const zone = zones.find(z => z.id === change.id)
            if (zone) {
              updateZone({ ...zone, position: change.position })
            }
          } else {
            updateDevicePosition(change.id, change.position)
          }
        }
      })
    },
    [onNodesChange, updateDevicePosition, updateZone, zones],
  )

  // Drag-and-drop between zones. Two ways to move a device:
  //   (a) Explicit: drop onto a floating "Move to …" drop target
  //       (see the `data-drop-zone-target` panel below). Works in
  //       both overview and drilldown modes.
  //   (b) Implicit: in overview mode only, drop onto a visible zone
  //       card. The card intersection check is skipped in drilldown
  //       because no zone cards render there — leaving it on made
  //       every drag accidentally pop the device out to unassigned.
  const handleNodeDragStop = useCallback(
    (event: React.MouseEvent | MouseEvent, node: Node) => {
      if (isReadOnly) return
      const device = devices.find(d => d.id === node.id)
      if (!device) return

      // (a) Explicit drop target lookup via the DOM element under
      // the cursor at drop time.
      const underCursor = document.elementFromPoint(
        event.clientX,
        event.clientY,
      )
      const dropTarget = underCursor?.closest('[data-drop-zone-target]')
      if (dropTarget instanceof HTMLElement) {
        const raw = dropTarget.dataset.dropZoneTarget
        const targetZoneId = raw === '__unassigned__' ? undefined : raw
        if (targetZoneId !== device.zoneId) {
          updateDevice({ ...device, zoneId: targetZoneId })
        }
        return
      }

      // (b) Overview zone-card intersection fallback. Drilldown
      // never pops devices out implicitly.
      if (focusedZoneId) return
      const intersecting = getIntersectingNodes(node).filter(
        n => n.type === 'zone',
      )
      const targetZoneId = intersecting[0]?.id
      if (targetZoneId === device.zoneId) return
      if (targetZoneId) {
        updateDevice({ ...device, zoneId: targetZoneId })
      }
    },
    [devices, focusedZoneId, getIntersectingNodes, updateDevice, isReadOnly],
  )

  const handleConnect: OnConnect = useCallback(
    (connection: Connection) => {
      if (isReadOnly) return
      if (
        !connection.source ||
        !connection.target ||
        !connection.sourceHandle ||
        !connection.targetHandle
      )
        return

      // Dedupe guard used by all three addConnection paths below.
      // Returns true if the exact (src device+port, tgt device+port)
      // pair already exists in the store — in which case the caller
      // should return early instead of creating a duplicate row.
      // Duplicate rows were rendering as "Nx" bundle count badges
      // on overview edges, which looked like a bug to users.
      const isDuplicate = (
        srcDeviceId: string,
        srcPortId: string,
        tgtDeviceId: string,
        tgtPortId: string,
      ): boolean =>
        connections.some(
          c =>
            c.sourceDeviceId === srcDeviceId &&
            c.sourcePortId === srcPortId &&
            c.targetDeviceId === tgtDeviceId &&
            c.targetPortId === tgtPortId,
        )

      const sourceIsWall = isWallNodeId(connection.source)
      const targetIsWall = isWallNodeId(connection.target)
      const sourceIsSat = isPeerSatelliteNodeId(connection.source)
      const targetIsSat = isPeerSatelliteNodeId(connection.target)

      // Wall ↔ peer satellite drag: create a real cross-zone
      // device-to-device Connection. Both endpoints resolve to
      // inner (device, port) via the focused zone's cps (wall
      // side) and the peer zone's cps (satellite side). No new
      // ZoneConnectionPoints are created — both sides must
      // already be exposed, which they are by virtue of having
      // rendered handles.
      if ((sourceIsWall && targetIsSat) || (targetIsWall && sourceIsSat)) {
        const wallNodeId = sourceIsWall ? connection.source : connection.target
        const satNodeId = sourceIsSat ? connection.source : connection.target
        const wallHandleId = sourceIsWall
          ? connection.sourceHandle
          : connection.targetHandle
        const satHandleId = sourceIsSat
          ? connection.sourceHandle
          : connection.targetHandle
        if (!wallHandleId || !satHandleId) return
        // Wall cp lives on the focused zone. The wall handle id
        // may be the bare cp id (legacy, or an internal handle)
        // or the external `<cpId>::ext` form. Either resolves to
        // the same cp.
        const { cpId: wallCpId } = stripExtHandleSuffix(wallHandleId)
        const focusedCp = focusedZone?.connectionPoints?.find(
          cp => cp.id === wallCpId,
        )
        if (!focusedCp) return
        const focusedInnerDevice = devices.find(
          d => d.id === focusedCp.internalDeviceId,
        )
        const focusedInnerPort =
          focusedInnerDevice?.inputs.find(
            p => p.id === focusedCp.internalPortId,
          ) ||
          focusedInnerDevice?.outputs.find(
            p => p.id === focusedCp.internalPortId,
          )
        if (!focusedInnerDevice || !focusedInnerPort) return
        // Satellite cp lives on the peer zone. Look the satellite
        // node up in React Flow and read peerZoneId from its data
        // rather than parsing the node id — zone ids contain '-'
        // characters, so splitting the id on '-' mis-identifies
        // the peer (the previous approach silently dropped the
        // connection when this happened).
        const satNode = getNode(satNodeId)
        const peerZoneId = (satNode?.data as PeerSatelliteData | undefined)
          ?.peerZoneId
        if (!peerZoneId) return
        const peerZone = zones.find(z => z.id === peerZoneId)
        const peerCp = peerZone?.connectionPoints?.find(
          cp => cp.id === satHandleId,
        )
        if (!peerZone || !peerCp) return
        const peerInnerDevice = devices.find(
          d => d.id === peerCp.internalDeviceId,
        )
        const peerInnerPort =
          peerInnerDevice?.inputs.find(p => p.id === peerCp.internalPortId) ||
          peerInnerDevice?.outputs.find(p => p.id === peerCp.internalPortId)
        if (!peerInnerDevice || !peerInnerPort) return
        // Figure out signal-flow direction from the wall side:
        // focused output wall means focused → peer, focused input
        // wall means peer → focused.
        const focusedWallKind = wallKindFromNodeId(wallNodeId)
        if (!focusedWallKind) return
        const storedSourceDev =
          focusedWallKind === 'output' ? focusedInnerDevice : peerInnerDevice
        const storedSourcePort =
          focusedWallKind === 'output' ? focusedInnerPort : peerInnerPort
        const storedTargetDev =
          focusedWallKind === 'output' ? peerInnerDevice : focusedInnerDevice
        const storedTargetPort =
          focusedWallKind === 'output' ? peerInnerPort : focusedInnerPort
        if (!validateConnection(storedSourcePort, storedTargetPort)) {
          alert(
            `Signal type mismatch: ${storedSourcePort.signalType} cannot connect to ${storedTargetPort.signalType}.`,
          )
          return
        }
        if (
          isDuplicate(
            storedSourceDev.id,
            storedSourcePort.id,
            storedTargetDev.id,
            storedTargetPort.id,
          )
        ) {
          return
        }
        addConnection({
          id: `conn-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
          sourceDeviceId: storedSourceDev.id,
          sourcePortId: storedSourcePort.id,
          targetDeviceId: storedTargetDev.id,
          targetPortId: storedTargetPort.id,
          signalType: storedSourcePort.signalType,
        })
        return
      }

      // Wall drop detection. If either endpoint is one of the
      // synthetic wall nodes that live only in drilldown, don't
      // create a Connection — instead, promote the dragged device
      // port into a new ZoneConnectionPoint on the focused zone.
      // The wall is UI for exposing a port, not a real endpoint.
      if (sourceIsWall || targetIsWall) {
        const wallNodeId = sourceIsWall ? connection.source : connection.target
        const wallKind = wallKindFromNodeId(wallNodeId)
        if (!wallKind) return
        // The non-wall endpoint must be a real device+port, since
        // wall↔wall doesn't make sense.
        const deviceNodeId = sourceIsWall
          ? connection.target
          : connection.source
        const devicePortId = sourceIsWall
          ? connection.targetHandle
          : connection.sourceHandle
        const device = devices.find(d => d.id === deviceNodeId)
        if (!device || !devicePortId) return
        const port =
          device.inputs.find(p => p.id === devicePortId) ||
          device.outputs.find(p => p.id === devicePortId)
        if (!port) return
        const portDirection: 'input' | 'output' = device.inputs.some(
          p => p.id === port.id,
        )
          ? 'input'
          : 'output'
        if (portDirection !== wallKind) {
          alert(
            `Drag ${portDirection === 'input' ? 'an input' : 'an output'} port onto the ${portDirection} wall, not the ${wallKind} wall.`,
          )
          return
        }
        if (!device.zoneId) {
          alert('Only devices inside a zone can be exposed on its wall.')
          return
        }
        const zone = zones.find(z => z.id === device.zoneId)
        if (!zone) return
        const already = (zone.connectionPoints ?? []).some(
          cp =>
            cp.internalDeviceId === device.id && cp.internalPortId === port.id,
        )
        if (already) return
        const existingCount = (zone.connectionPoints ?? []).filter(
          cp => cp.direction === wallKind,
        ).length
        const newCp = {
          id: `cp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          name: `${device.name} · ${port.name}`,
          edge: (wallKind === 'input' ? 'left' : 'right') as
            | 'left'
            | 'right'
            | 'top'
            | 'bottom',
          direction: wallKind,
          signalType: port.signalType,
          internalDeviceId: device.id,
          internalPortId: port.id,
          // Spread exposures out along the zone edge so the zone
          // card's handles (used in overview) don't stack on top of
          // each other.
          position: Math.min(0.95, 0.05 + existingCount * 0.12),
        }
        updateZone({
          ...zone,
          connectionPoints: [...(zone.connectionPoints ?? []), newCp],
        })
        return
      }

      // Resolve the React Flow endpoint (node id + handle id) into
      // a concrete device + port. An endpoint may be a zone, in
      // which case the handle id is a ZoneConnectionPoint id that
      // points at an internal device port. This lets the user draw
      // zone↔zone or zone↔device connections and have them recorded
      // as real device-to-device edges under the hood.
      const resolveEndpoint = (
        nodeId: string,
        handleId: string,
      ): { device: Device; port: Port } | null => {
        const dev = devices.find(d => d.id === nodeId)
        if (dev) {
          const port =
            dev.outputs.find(p => p.id === handleId) ||
            dev.inputs.find(p => p.id === handleId)
          if (!port) return null
          return { device: dev, port }
        }
        const zone = zones.find(z => z.id === nodeId)
        if (!zone) return null
        const cp = zone.connectionPoints?.find(p => p.id === handleId)
        if (!cp) return null
        const innerDev = devices.find(d => d.id === cp.internalDeviceId)
        if (!innerDev) return null
        const innerPort =
          innerDev.outputs.find(p => p.id === cp.internalPortId) ||
          innerDev.inputs.find(p => p.id === cp.internalPortId)
        if (!innerPort) return null
        return { device: innerDev, port: innerPort }
      }

      const src = resolveEndpoint(connection.source, connection.sourceHandle)
      const tgt = resolveEndpoint(connection.target, connection.targetHandle)
      if (!src || !tgt) return
      const sourceDevice = src.device
      const targetDevice = tgt.device
      const sourcePort = src.port
      const targetPort = tgt.port

      if (!validateConnection(sourcePort, targetPort)) {
        console.warn(
          `Invalid connection: ${sourcePort.signalType} !== ${targetPort.signalType}`,
        )
        return
      }

      // Cross-zone enforcement. Any device that belongs to a zone
      // must have its port exposed at the zone boundary before it
      // can participate in a cross-zone connection. Connections
      // between devices that share a zone, or between two
      // unassigned devices, are always allowed.
      const isPortExposed = (deviceId: string, portId: string): boolean => {
        const dev = devices.find(d => d.id === deviceId)
        if (!dev?.zoneId) return true
        const zone = zones.find(z => z.id === dev.zoneId)
        return !!zone?.connectionPoints?.some(
          cp =>
            cp.internalDeviceId === deviceId && cp.internalPortId === portId,
        )
      }
      if (sourceDevice.zoneId !== targetDevice.zoneId) {
        if (!isPortExposed(sourceDevice.id, sourcePort.id)) {
          alert(
            `Cannot connect: ${sourcePort.name} on ${sourceDevice.name} is not exposed on its zone boundary. Edit the zone to expose it.`,
          )
          return
        }
        if (!isPortExposed(targetDevice.id, targetPort.id)) {
          alert(
            `Cannot connect: ${targetPort.name} on ${targetDevice.name} is not exposed on its zone boundary. Edit the zone to expose it.`,
          )
          return
        }
      }

      // Store the *resolved* device/port ids, not the React Flow
      // node/handle ids. When an endpoint is a zone, `connection.source`
      // is the zone id and `connection.sourceHandle` is a
      // ZoneConnectionPoint id — neither can be looked up as a device,
      // so the edge projection would silently drop the connection on
      // the next render. Using the inner device+port keeps the edge
      // consistent with the persistence model.
      if (
        isDuplicate(
          sourceDevice.id,
          sourcePort.id,
          targetDevice.id,
          targetPort.id,
        )
      ) {
        return
      }
      addConnection({
        id: `conn-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        sourceDeviceId: sourceDevice.id,
        sourcePortId: sourcePort.id,
        targetDeviceId: targetDevice.id,
        targetPortId: targetPort.id,
        signalType: sourcePort.signalType,
      })
    },
    [
      devices,
      connections,
      addConnection,
      zones,
      isReadOnly,
      updateZone,
      focusedZone,
      getNode,
    ],
  )

  // Scope devices/connections/zones to the current viewport: when
  // drilled into a zone, suggestions only consider devices in that
  // zone and the connections wholly inside it. Otherwise use the
  // entire layout. Both Auto-Map and Smart Route reuse this so
  // behaviour stays consistent across the two paths.
  const scopedSuggestionContext = useMemo(() => {
    if (!focusedZoneId) {
      return { devices, connections, zones }
    }
    const scopedDevices = devices.filter(d => d.zoneId === focusedZoneId)
    const deviceIdSet = new Set(scopedDevices.map(d => d.id))
    const scopedConnections = connections.filter(
      c =>
        deviceIdSet.has(c.sourceDeviceId) && deviceIdSet.has(c.targetDeviceId),
    )
    const scopedZones = zones.filter(z => z.id === focusedZoneId)
    return {
      devices: scopedDevices,
      connections: scopedConnections,
      zones: scopedZones,
    }
  }, [devices, connections, zones, focusedZoneId])

  // Auto-Map handler
  const handleAutoMap = useCallback(() => {
    const { devices: scopedDevices, connections: scopedConnections } =
      scopedSuggestionContext
    const results = generateSuggestions(scopedDevices, scopedConnections)
    if (results.length === 0) {
      alert(
        focusedZoneId
          ? 'No new connections to suggest inside this zone. Exit drilldown to auto-map the full layout.'
          : 'No new connections to suggest. Everything looks wired up!',
      )
      return
    }
    setSuggestions(results)
    setShowSuggestions(true)
  }, [scopedSuggestionContext, focusedZoneId])

  // "Smart Route" (AI path) — send the current viewport snapshot to
  // Bedrock and surface the result through the same AutoMapPanel the
  // rule engine uses. Gated on both local-dev (no cloud layout to
  // send) and the user's tier flag — free plans don't get AI
  // suggestions and fall back to the offline rule engine instead.
  // The tier check reads from useAuth() which pulls from
  // /api/v1/users/me, which in turn re-derives features from
  // TIER_FEATURES on every auth check. So the gate flips on the
  // instant Stripe Checkout fires user.subscription.updated.
  const aiSuggest = useAiSuggest(isLocalDev ? null : id)
  const aiSuggestionsAllowed = features?.aiSuggestions ?? false
  const aiAvailable = !isLocalDev && aiSuggestionsAllowed
  const askAiDisabledReason = isLocalDev
    ? 'Smart Route is only available for cloud layouts (not /editor/local-dev)'
    : !aiSuggestionsAllowed
      ? 'Smart Route is a Pro feature — upgrade your plan to enable it'
      : undefined

  const handleAskAi = useCallback(async () => {
    if (!aiAvailable) return
    try {
      const response = await aiSuggest.mutateAsync({
        devices: scopedSuggestionContext.devices,
        connections: scopedSuggestionContext.connections,
        zones: scopedSuggestionContext.zones as never,
      })
      const raw = response?.suggestions ?? []
      if (raw.length === 0) {
        alert('The AI did not return any new suggestions for this layout.')
        return
      }

      // Pre-compute the set of "unavailable" (deviceId, portId) keys.
      // A port is unavailable if it's directly wired OR if any port
      // on the same device sharing its exclusiveGroup is wired (the
      // Mackie TRS/XLR case: wiring the TRS main out should also
      // block suggestions that use the XLR sibling). Belt-and-braces
      // filter on top of the server-side prompt rule — Claude can
      // still violate the exclusive-group rule, and this catches it.
      const unavailable = new Set<string>()
      for (const conn of connections) {
        unavailable.add(`${conn.sourceDeviceId}:${conn.sourcePortId}`)
        unavailable.add(`${conn.targetDeviceId}:${conn.targetPortId}`)
        const srcDev = devices.find(d => d.id === conn.sourceDeviceId)
        const srcPort = srcDev?.outputs.find(p => p.id === conn.sourcePortId)
        if (srcDev && srcPort?.exclusiveGroup) {
          for (const sib of srcDev.outputs) {
            if (sib.exclusiveGroup === srcPort.exclusiveGroup) {
              unavailable.add(`${srcDev.id}:${sib.id}`)
            }
          }
        }
        const tgtDev = devices.find(d => d.id === conn.targetDeviceId)
        const tgtPort = tgtDev?.inputs.find(p => p.id === conn.targetPortId)
        if (tgtDev && tgtPort?.exclusiveGroup) {
          for (const sib of tgtDev.inputs) {
            if (sib.exclusiveGroup === tgtPort.exclusiveGroup) {
              unavailable.add(`${tgtDev.id}:${sib.id}`)
            }
          }
        }
      }

      // Translate id-only AI suggestions into the SuggestedConnection
      // shape the AutoMapPanel expects by looking each endpoint up in
      // the current device state. Drop any suggestion whose port
      // refs don't resolve, or whose endpoints are already in use
      // (directly or via an exclusive-group sibling).
      const enriched: SuggestedConnection[] = []
      raw.forEach((s: AILayoutSuggestion, idx: number) => {
        const sourceDevice = devices.find(d => d.id === s.sourceDeviceId)
        const targetDevice = devices.find(d => d.id === s.targetDeviceId)
        if (!sourceDevice || !targetDevice) return
        const sourcePort = sourceDevice.outputs.find(
          p => p.id === s.sourcePortId,
        )
        const targetPort = targetDevice.inputs.find(
          p => p.id === s.targetPortId,
        )
        if (!sourcePort || !targetPort) return
        if (unavailable.has(`${sourceDevice.id}:${sourcePort.id}`)) return
        if (unavailable.has(`${targetDevice.id}:${targetPort.id}`)) return
        enriched.push({
          id: `ai-${idx}-${sourceDevice.id}-${targetDevice.id}`,
          sourceDevice,
          targetDevice,
          sourcePort,
          targetPort,
          signalType: sourcePort.signalType,
          reason: `✨ ${s.reason || 'AI suggestion'}`,
          priority: Math.round((s.confidence ?? 0.7) * 100),
        })
      })

      if (enriched.length === 0) {
        alert(
          'The AI returned suggestions, but none of the port ids matched your current devices. Try again after renaming or reloading the layout.',
        )
        return
      }
      setSuggestions(enriched)
      setShowSuggestions(true)
    } catch (err) {
      console.error('Smart Route failed', err)
      alert(
        err instanceof Error
          ? `Smart Route failed: ${err.message}`
          : 'Smart Route failed',
      )
    }
  }, [aiAvailable, aiSuggest, devices, connections, scopedSuggestionContext])

  const applySuggestions = useCallback(
    (selected: SuggestedConnection[]) => {
      // Dedupe against existing connections so accepting a Smart
      // Route or Auto-Map suggestion that already exists in the layout
      // doesn't add a duplicate row (which renders as an "Nx"
      // bundle count badge in overview mode).
      const existing = new Set(
        connections.map(
          c =>
            `${c.sourceDeviceId}|${c.sourcePortId}|${c.targetDeviceId}|${c.targetPortId}`,
        ),
      )
      selected.forEach(s => {
        const key = `${s.sourceDevice.id}|${s.sourcePort.id}|${s.targetDevice.id}|${s.targetPort.id}`
        if (existing.has(key)) return
        existing.add(key)
        addConnection({
          id: `conn-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
          sourceDeviceId: s.sourceDevice.id,
          sourcePortId: s.sourcePort.id,
          targetDeviceId: s.targetDevice.id,
          targetPortId: s.targetPort.id,
          signalType: s.signalType,
        })
      })
      setShowSuggestions(false)
      setSuggestions([])
    },
    [addConnection, connections],
  )

  // Auto-Layout handler
  const handleAutoLayout = useCallback(() => {
    const layouted = getLayoutedElements(nodesState, edgesState, {
      direction: 'LR',
      nodeSpacing: 80,
      rankSpacing: 180,
    })

    setNodes(layouted.nodes)
    // Update positions in the store
    layouted.nodes.forEach(node => {
      if (node.position) {
        updateDevicePosition(node.id, node.position)
      }
    })

    // Reframe the canvas around the freshly-laid-out nodes after React
    // Flow has had a tick to apply the new positions.
    setTimeout(() => fitView({ padding: 0.2, duration: 400 }), 50)
  }, [nodesState, edgesState, setNodes, updateDevicePosition, fitView])

  // Fit the viewport whenever the device count grows (a new device was
  // added). The timeout gives React Flow one paint to position the
  // fresh node before we ask it to frame everything.
  useEffect(() => {
    if (!initialized) {
      prevDeviceCountRef.current = devices.length
      return
    }
    if (devices.length > prevDeviceCountRef.current) {
      const t = setTimeout(() => fitView({ padding: 0.2, duration: 400 }), 50)
      prevDeviceCountRef.current = devices.length
      return () => clearTimeout(t)
    }
    prevDeviceCountRef.current = devices.length
  }, [devices.length, initialized, fitView])

  // Reframe the canvas whenever the user enters or exits a zone drilldown.
  useEffect(() => {
    if (!initialized) return
    const t = setTimeout(() => fitView({ padding: 0.2, duration: 400 }), 50)
    return () => clearTimeout(t)
  }, [focusedZoneId, initialized, fitView])

  if (!initialized) {
    return (
      <div className="flex items-center justify-center h-[100dvh] bg-surface-base">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent" />
      </div>
    )
  }

  return (
    <LayoutRoom layoutId={id}>
      <div className="h-[100dvh] flex flex-col bg-surface-base touch-no-select">
        {/* Top safe-area spacer — pushes the header below the iOS
          status bar / Dynamic Island / macOS title bar. On web this
          collapses to zero. */}
        <div className="pt-safe bg-surface-overlay" />
        {/* Navigation Header */}
        <header className="h-10 bg-surface-overlay border-b border-brd-subtle flex items-center justify-between px-4 py-1.5">
          <div className="flex items-center gap-4">
            <BusswerksLogo size="sm" className="text-content-primary" />
            <Link
              to="/home"
              className="flex items-center gap-1 text-xs text-content-secondary hover:text-content-primary transition-colors duration-150 px-2 py-1 rounded hover:bg-accent-muted"
              title="Back to home"
            >
              <span>←</span>
              <span>Back</span>
            </Link>
            <MaybePresenceDock />
          </div>
          <div className="text-xs text-content-secondary truncate max-w-[50%] flex items-center gap-1.5">
            <span className="truncate">{layoutName || 'Untitled Layout'}</span>
            {focusedZone && (
              <>
                <span className="text-content-tertiary">›</span>
                <span
                  className="truncate text-content-primary"
                  style={{ color: focusedZone.color }}
                >
                  {focusedZone.name}
                </span>
              </>
            )}
            {layoutRole !== 'owner' && (
              <span
                className={`px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider font-semibold ${
                  isReadOnly
                    ? 'bg-warning/20 text-warning border border-warning/40'
                    : 'bg-accent-muted text-accent border border-accent/40'
                }`}
                title={
                  isReadOnly
                    ? 'You have viewer access — edits are disabled'
                    : 'You have editor access — you can save changes'
                }
              >
                Shared · {layoutRole}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <div className="text-xs text-content-tertiary">
              {isLocalDev
                ? 'Auto-saved (local)'
                : saveStatus === 'saving'
                  ? 'Saving…'
                  : saveStatus === 'error'
                    ? `Save failed${saveError ? `: ${saveError}` : ''}`
                    : saveStatus === 'saved'
                      ? 'Saved'
                      : 'Auto-save ready'}
            </div>
            <UserMenu />
          </div>
        </header>

        <CanvasToolbar
          onAddDevice={() => setAddDeviceOpen(true)}
          onAutoMap={handleAutoMap}
          onAskAi={handleAskAi}
          isAskAiPending={aiSuggest.isPending}
          isAskAiAvailable={aiAvailable}
          askAiDisabledReason={askAiDisabledReason}
          onAutoLayout={handleAutoLayout}
          onManageDevices={() => setManageDevicesOpen(true)}
          onMidiPanel={toggleMidiPanel}
          isMidiPanelOpen={midiPanelOpen}
          isReadOnly={isReadOnly}
        />

        {/* Zone Actions */}
        <div className="flex items-center gap-2 px-4 py-1 bg-surface-raised border-b border-brd-subtle">
          {focusedZoneId ? (
            <>
              <button
                onClick={() => setFocusedZoneId(null)}
                className="text-xs px-3 py-1 bg-accent text-white rounded-md hover:bg-accent-hover transition-colors duration-150 font-medium flex items-center gap-1"
                title="Return to the full layout view"
              >
                <span>←</span> Show all zones
              </button>
              {focusedZone && !isReadOnly && (
                <button
                  onClick={() => openEditZone(focusedZone)}
                  className="text-xs px-3 py-1 bg-surface-sunken text-content-primary rounded-md hover:bg-surface-overlay border border-brd-subtle transition-colors duration-150 font-medium flex items-center gap-1"
                  title="Edit this zone's name, members, or boundary ports"
                >
                  <span>✏</span> Edit zone
                </button>
              )}
            </>
          ) : (
            !isReadOnly && (
              <button
                onClick={() => {
                  setEditingZone(null)
                  setZoneDialogOpen(true)
                }}
                className="text-xs px-3 py-1 bg-accent text-white rounded-md hover:bg-accent-hover transition-colors duration-150 font-medium"
              >
                + New Zone
              </button>
            )
          )}
          {zones.map(zone => (
            <button
              key={zone.id}
              onClick={() =>
                setFocusedZoneId(focusedZoneId === zone.id ? null : zone.id)
              }
              className={`text-xs px-3 py-1 rounded-md border transition-all duration-150 ${
                focusedZoneId === zone.id
                  ? 'ring-2 ring-offset-1 ring-offset-surface-base'
                  : 'opacity-70 hover:opacity-100'
              }`}
              style={{
                borderColor: zone.color,
                color: zone.color,
                backgroundColor:
                  focusedZoneId === zone.id ? `${zone.color}20` : 'transparent',
              }}
            >
              {zone.name}
            </button>
          ))}
          {focusedZoneId && visibleDeviceIds && (
            <span className="text-xs text-content-secondary ml-auto">
              Showing {visibleDeviceIds.size} device
              {visibleDeviceIds.size === 1 ? '' : 's'} in {focusedZone?.name}
            </span>
          )}
        </div>

        <PresenceCanvasOverlay className="flex-1 relative">
          {viewMode === 'graph' && (
            <>
              <ReactFlow
                nodes={nodesState}
                edges={edgesState}
                onNodesChange={handleNodesChange}
                onNodeDragStop={handleNodeDragStop}
                onEdgesChange={onEdgesChange}
                onConnect={handleConnect}
                nodeTypes={nodeTypes}
                edgeTypes={edgeTypes}
                nodesDraggable={!isReadOnly}
                nodesConnectable={!isReadOnly}
                fitView
                minZoom={0.1}
                maxZoom={2}
                // Touch tuning for iOS / iPadOS (same binary runs on
                // Mac + Vision Pro via "Designed for iPad"):
                // - `panOnDrag` lets a single finger drag pan the canvas
                //   (as opposed to starting a selection rectangle).
                // - `selectionOnDrag=false` prevents the drag-to-select
                //   gesture stealing touches from the pan.
                // - `panOnScroll=false` because two-finger trackpad
                //   scrolling shouldn't pan; pinch-to-zoom handles that.
                // - `zoomOnPinch` is default but set explicitly so a
                //   future React Flow upgrade can't silently flip it.
                // - `preventScrolling` stops the outer page from
                //   scrolling when a two-finger drag lands on the canvas.
                panOnDrag
                selectionOnDrag={false}
                panOnScroll={false}
                zoomOnPinch
                preventScrolling
                defaultEdgeOptions={{
                  type: 'connection',
                  style: { strokeWidth: 2 },
                  animated: false,
                }}
                connectionLineType={ConnectionLineType.Bezier}
                proOptions={{ hideAttribution: true }}
              >
                <Background gap={20} size={1} />
                <Controls />
              </ReactFlow>

              {/* Empty-layout onboarding prompt for new cloud layouts */}
              {devices.length === 0 && !isLocalDev && !isReadOnly && (
                <div className="absolute inset-0 flex items-center justify-center z-30 pointer-events-none">
                  <div className="text-center pointer-events-auto bg-surface-raised/95 backdrop-blur-sm rounded-2xl p-8 shadow-high border border-brd-subtle max-w-sm">
                    <div className="mx-auto w-14 h-14 rounded-xl bg-accent-muted flex items-center justify-center mb-4">
                      <svg
                        className="w-7 h-7 text-accent"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={1.5}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 4.5v15m7.5-7.5h-15"
                        />
                      </svg>
                    </div>
                    <h3 className="text-lg font-bold text-content-primary mb-2">
                      Your layout is empty
                    </h3>
                    <p className="text-sm text-content-secondary mb-5">
                      Add your first device to start mapping your audio signal
                      flow.
                    </p>
                    <button
                      onClick={() => setAddDeviceOpen(true)}
                      className="btn-primary btn-lg font-semibold rounded-xl shadow-medium"
                    >
                      Add Device
                    </button>
                  </div>
                </div>
              )}

              {/* Demo hints card — /editor/local-dev only */}
              {isLocalDev && <DemoCallout />}

              {/* Auto-Map Suggestions Panel */}
              {showSuggestions && suggestions.length > 0 && (
                <AutoMapPanel
                  suggestions={suggestions}
                  onApply={applySuggestions}
                  onClose={() => setShowSuggestions(false)}
                />
              )}

              {/* MIDI Control Panel */}
              {midiPanelOpen && <MidiPanel onClose={toggleMidiPanel} />}

              {/* Floating zone drop-target panel. Drag a device onto
                one of these pills to explicitly reassign it to a
                zone (or to unassigned). data-drop-zone-target is
                read by handleNodeDragStop via elementFromPoint. */}
              {zones.length > 0 && !isReadOnly && (
                <div className="absolute top-4 left-4 flex flex-col gap-1 pointer-events-none z-40">
                  <span className="text-[10px] uppercase tracking-wider text-content-tertiary font-semibold pointer-events-none">
                    Move to…
                  </span>
                  <div className="flex flex-col gap-1 pointer-events-auto">
                    <button
                      type="button"
                      data-drop-zone-target="__unassigned__"
                      className="text-xs px-3 py-1.5 rounded-lg bg-surface-overlay/90 border border-brd-emphasis text-content-primary hover:bg-surface-overlay transition-colors duration-150 text-left shadow-medium backdrop-blur-sm"
                      title="Drag a device onto this pill to leave it unassigned"
                    >
                      Unassigned
                    </button>
                    {zones
                      .filter(z => z.id !== focusedZoneId)
                      .map(z => (
                        <button
                          key={z.id}
                          type="button"
                          data-drop-zone-target={z.id}
                          className="text-xs px-3 py-1.5 rounded-lg border text-white text-left shadow-md transition"
                          style={{
                            backgroundColor: `${z.color}30`,
                            borderColor: z.color,
                            color: z.color,
                          }}
                          title={`Drag a device onto this pill to move it to ${z.name}`}
                        >
                          {z.name}
                        </button>
                      ))}
                  </div>
                </div>
              )}
            </>
          )}

          {viewMode === 'matrix' && (
            <RoutingMatrix
              devices={devices}
              connections={connections}
              onDeleteConnection={removeConnection}
            />
          )}

          {viewMode === 'list' && (
            <ConnectionList
              devices={devices}
              connections={connections}
              onDeleteConnection={removeConnection}
            />
          )}
        </PresenceCanvasOverlay>

        {/* Status Bar — `pb-safe` keeps the footer text above the
            iOS home indicator on native. Collapses to 0 on web. */}
        <div className="bg-surface-raised border-t border-brd-subtle px-4 py-2 pb-safe text-sm text-content-secondary">
          <div className="flex justify-between items-center">
            <div>
              Devices: {devices.length} | Connections: {connections.length}
              {zones.length > 0 && ` | Zones: ${zones.length}`}
            </div>
            <div className="text-xs">
              Layout: {layoutName || 'Untitled'}
              {isLocalDev
                ? ' | Auto-saved to localStorage'
                : saveStatus === 'saving'
                  ? ' | Saving…'
                  : saveStatus === 'error'
                    ? ` | Save failed${saveError ? `: ${saveError}` : ''}`
                    : saveStatus === 'saved'
                      ? ' | Saved to cloud'
                      : ' | Cloud layout'}
            </div>
          </div>
        </div>

        {/* Add Device Dialog */}
        <AddDeviceDialog
          isOpen={addDeviceOpen}
          onClose={() => setAddDeviceOpen(false)}
        />

        {/* Manage Devices Panel */}
        <ManageDevicesPanel
          isOpen={manageDevicesOpen}
          onClose={() => setManageDevicesOpen(false)}
        />

        {/* Manage Zones Dialog */}
        <ManageZonesDialog
          // Force-remount whenever the editing target or open state
          // flips so the dialog's internal useState initializers re-run
          // with the current editingZone, instead of showing stale
          // form state from the last time it was opened.
          key={`zone-dialog-${editingZone?.id ?? 'new'}-${zoneDialogOpen ? 'open' : 'closed'}`}
          isOpen={zoneDialogOpen}
          onClose={() => setZoneDialogOpen(false)}
          editingZone={editingZone}
        />
      </div>
    </LayoutRoom>
  )
}

/**
 * Auto-Map suggestions overlay panel
 */
function AutoMapPanel({
  suggestions,
  onApply,
  onClose,
}: {
  suggestions: SuggestedConnection[]
  onApply: (selected: SuggestedConnection[]) => void
  onClose: () => void
}) {
  const [selected, setSelected] = useState<Set<string>>(
    new Set(suggestions.map(s => s.id)),
  )

  const toggle = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const selectedSuggestions = suggestions.filter(s => selected.has(s.id))

  // Group by signal type
  const grouped = suggestions.reduce(
    (acc, s) => {
      const key = s.signalType
      if (!acc[key]) acc[key] = []
      acc[key].push(s)
      return acc
    },
    {} as Record<string, SuggestedConnection[]>,
  )

  return (
    <div className="absolute top-4 right-4 w-96 max-h-[80vh] bg-surface-raised rounded-xl shadow-high border border-brd-subtle overflow-hidden z-50 flex flex-col">
      <div className="px-4 py-3 bg-accent text-[color:var(--accent-ink)]">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-sm">
            Auto-Map Suggestions ({suggestions.length})
          </h3>
          <button
            onClick={onClose}
            className="opacity-80 hover:opacity-100 text-lg leading-none"
          >
            x
          </button>
        </div>
        <p className="text-xs opacity-80 mt-1">
          Review suggested connections and apply
        </p>
      </div>

      <div className="flex-1 overflow-y-auto">
        {Object.entries(grouped).map(([signalType, items]) => (
          <div key={signalType}>
            <div
              className="px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-white"
              style={{
                backgroundColor:
                  SIGNAL_TYPE_COLORS[
                    signalType as keyof typeof SIGNAL_TYPE_COLORS
                  ] || '#999',
              }}
            >
              {signalType.replace('_', '/')} ({items.length})
            </div>
            {items.map(s => (
              <label
                key={s.id}
                className={`flex items-start gap-3 px-4 py-2.5 border-b border-brd-subtle hover:bg-accent-muted cursor-pointer transition-colors duration-150 ${
                  selected.has(s.id) ? '' : 'opacity-50'
                }`}
              >
                <input
                  type="checkbox"
                  checked={selected.has(s.id)}
                  onChange={() => toggle(s.id)}
                  className="mt-0.5 rounded"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-content-primary truncate">
                    {s.sourceDevice.name}{' '}
                    <span className="text-content-tertiary">→</span>{' '}
                    {s.targetDevice.name}
                  </div>
                  <div className="text-[10px] text-content-secondary">
                    {s.sourcePort.name} → {s.targetPort.name}
                  </div>
                  <div className="text-[10px] text-content-tertiary italic mt-0.5">
                    {s.reason}
                  </div>
                </div>
                <span className="text-[10px] text-content-tertiary whitespace-nowrap mt-0.5">
                  P{s.priority}
                </span>
              </label>
            ))}
          </div>
        ))}
      </div>

      <div className="px-4 py-3 bg-surface-sunken border-t border-brd-subtle flex items-center justify-between">
        <div className="flex gap-2">
          <button
            onClick={() => setSelected(new Set(suggestions.map(s => s.id)))}
            className="text-xs text-accent hover:underline"
          >
            Select All
          </button>
          <button
            onClick={() => setSelected(new Set())}
            className="text-xs text-content-tertiary hover:underline"
          >
            Clear
          </button>
        </div>
        <button
          onClick={() => onApply(selectedSuggestions)}
          disabled={selectedSuggestions.length === 0}
          className="btn btn-sm bg-accent text-[color:var(--accent-ink)] hover:bg-[color:var(--accent-hover)] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Apply {selectedSuggestions.length} Connection
          {selectedSuggestions.length !== 1 ? 's' : ''}
        </button>
      </div>
    </div>
  )
}
